import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

/**
 * Visual seat plan: 18 rows × 12 seats grid.
 * Colors:
 *   - light grey: free (no reservation)
 *   - amber: reserved (open, for the currently selected reservation it appears blue)
 *   - emerald: bezahlt / abgeholt
 *   - violet ring: currently in cart
 *
 * Click behaviour:
 *   - For WALK-IN mode (selectedRes === null): a click on a free seat selects/deselects it.
 *   - For RES mode (selectedRes set): a click on an open seat of that reservation selects/deselects it.
 *   - All other seat clicks are no-ops (locked).
 */
export default function SeatPlan({
  showId,
  selectedRes,
  refreshKey = 0,
  selectedSeats = new Set(), // Set of "row-seat" strings currently in cart
  onSeatClick = null, // (seatInfo) => void; if null, plan is read-only
  paidClickable = false, // when true (Retoure-Modus), paid seats are clickable
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/shows/${showId}/seatmap`);
      setData(data);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, [showId]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  if (loading && !data) {
    return <div className="text-sm text-zinc-500 p-3">Lade Saalplan…</div>;
  }
  if (!data) return null;

  const rows = data.rows || 18;
  const seatsPerRow = data.seats_per_row || 12;

  const byKey = new Map();
  for (const s of data.seats || []) {
    byKey.set(`${s.row}-${s.seat}`, s);
  }

  // Stats
  let reserved = 0, paid = 0, restplatz = 0;
  for (const s of data.seats || []) {
    if (s.is_restplatz) restplatz++;
    if (s.status === "paid") paid++;
    else if (s.status === "reserved") reserved++;
  }
  const free = (data.total || rows * seatsPerRow) - reserved - paid;

  const handleClick = (key, entry, isClickable) => {
    if (!onSeatClick || !isClickable) return;
    onSeatClick({
      key,
      row: parseInt(key.split("-")[0], 10),
      seat: parseInt(key.split("-")[1], 10),
      status: entry?.status || "free",
      reservation_id: entry?.reservation_id || null,
      reservation_name: entry?.reservation_name || "",
      is_restplatz: !!entry?.is_restplatz,
    });
  };

  // Striped pattern for RESTPLATZ seats — recognizable "emergency only" marker.
  const restplatzStripes = "repeating-linear-gradient(45deg, rgba(0,0,0,0.25) 0 2px, transparent 2px 4px)";

  return (
    <div className="flex flex-col h-full" data-testid="kassa-seatplan">
      <div className="bg-zinc-900 text-white text-sm tracking-[0.4em] font-bold text-center py-2 rounded-t-md uppercase">
        Bühne
      </div>

      <div className="bg-zinc-50 border-x border-zinc-200 px-2 py-3 overflow-auto flex-1">
        <table className="mx-auto border-separate" style={{ borderSpacing: 3 }}>
          <thead>
            <tr>
              <th className="w-6"></th>
              {Array.from({ length: seatsPerRow }).map((_, si) => (
                <th key={si + 1} className="w-7 md:w-9 text-[10px] md:text-xs font-bold text-zinc-500 text-center align-bottom pb-1">
                  {si + 1}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: rows }).map((_, ri) => {
              const rowNum = ri + 1;
              return (
                <tr key={rowNum}>
                  <td className="text-xs text-zinc-500 font-bold pr-2 text-right align-middle w-6">{rowNum}</td>
                  {Array.from({ length: seatsPerRow }).map((__, si) => {
                    const seatNum = si + 1;
                    const key = `${rowNum}-${seatNum}`;
                    const entry = byKey.get(key);
                    const isInCart = selectedSeats.has(key);
                    const isOwnRes = entry?.status === "reserved" && entry.reservation_id === selectedRes?.id;
                    const isRestplatz = !!entry?.is_restplatz;

                    let bg = "bg-zinc-200";
                    let title = `Reihe ${rowNum}, Sitz ${seatNum} – frei`;
                    let isClickable = false;
                    let marker = null;

                    if (entry?.status === "paid") {
                      bg = "bg-emerald-500";
                      const via = entry.payment_type === "bank_transfer" ? " (Überweisung)" : "";
                      title = `Reihe ${rowNum}, Sitz ${seatNum} – bezahlt${via} – ${entry.reservation_name || "Abendkasse"}`;
                      if (entry.payment_type === "bank_transfer") marker = "Ü";
                      if (paidClickable) isClickable = !!onSeatClick;
                    } else if (entry?.status === "reserved") {
                      if (isOwnRes) {
                        bg = "bg-blue-400";
                        title = `Reihe ${rowNum}, Sitz ${seatNum} – Reservierung ${entry.reservation_name}`;
                        isClickable = !!onSeatClick;
                      } else {
                        bg = "bg-amber-400";
                        title = `Reihe ${rowNum}, Sitz ${seatNum} – reserviert (${entry.reservation_name})`;
                      }
                    } else {
                      if (!selectedRes) {
                        isClickable = !!onSeatClick;
                      }
                    }
                    if (isRestplatz) {
                      title = `${title} · RESTPLATZ (Notfall-Sitz, freie Sicht)`;
                    }
                    const cursor = isClickable ? "cursor-pointer hover:scale-110 active:scale-95" : "cursor-not-allowed";
                    const ring = isInCart ? "ring-4 ring-violet-700 ring-offset-1" : "";

                    return (
                      <td
                        key={seatNum}
                        title={title}
                        data-testid={`seat-${rowNum}-${seatNum}`}
                        onClick={() => handleClick(key, entry, isClickable)}
                        style={isRestplatz ? { backgroundImage: restplatzStripes } : undefined}
                        className={`w-7 h-7 md:w-9 md:h-9 rounded transition-transform ${bg} ${cursor} ${ring} relative text-white font-black text-xs text-center align-middle select-none p-0`}
                      >{marker}</td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Zusatz-Reihe (z.B. "3B" mit 14 Sitzen) — nur sichtbar wenn aktiviert */}
        {data.extra_row && (
          <div className="mt-4 max-w-fit mx-auto bg-amber-50 border-2 border-amber-300 rounded-lg p-2">
            <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-amber-900 text-center mb-1">
              Zusatz-Reihe {data.extra_row.label} · {data.extra_row.count} Notsitze
            </p>
            <table className="border-separate" style={{ borderSpacing: 3 }}>
              <tbody>
                <tr>
                  <td className="w-8 text-[10px] font-bold text-amber-800 text-right pr-1">{data.extra_row.label}</td>
                  {data.extra_row.seats.map((es) => {
                    const key = `${data.extra_row.label}-${es.seat}`;
                    const isSelected = selectedSeats.has(key);
                    let bg = "bg-zinc-200";
                    let isClickable = false;
                    let marker = es.seat;
                    if (es.status === "paid") {
                      bg = "bg-emerald-500 text-white";
                      if (paidClickable) isClickable = !!onSeatClick;
                      if (es.payment_type === "bank_transfer") marker = "Ü";
                    } else {
                      // free → clickable in walk-in mode (no reservation context)
                      if (!selectedRes) isClickable = !!onSeatClick;
                    }
                    if (isSelected) bg += " ring-2 ring-violet-600";
                    return (
                      <td key={es.seat}
                        title={`${data.extra_row.label}-${es.seat} (${es.status})`}
                        data-testid={`seat-${data.extra_row.label}-${es.seat}`}
                        onClick={() => {
                          if (!isClickable) return;
                          onSeatClick({
                            key, row: data.extra_row.label, seat: es.seat,
                            status: es.status, reservation_name: es.reservation_name,
                            payment_type: es.payment_type,
                          });
                        }}
                        className={`w-7 h-7 md:w-9 md:h-9 rounded transition-transform ${bg} ${isClickable ? "cursor-pointer hover:scale-110" : "cursor-default"} text-xs text-center align-middle font-black p-0`}
                      >{marker}</td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-white border border-t-0 border-zinc-200 rounded-b-md px-3 py-3 text-sm flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-4 flex-wrap">
          <span className="flex items-center gap-2">
            <span className="w-4 h-4 rounded bg-zinc-200" /> Frei <strong>{free}</strong>
          </span>
          <span className="flex items-center gap-2">
            <span className="w-4 h-4 rounded bg-amber-400" /> Reserviert <strong>{reserved}</strong>
          </span>
          <span className="flex items-center gap-2">
            <span className="w-4 h-4 rounded bg-emerald-500" /> Bezahlt <strong>{paid}</strong>
          </span>
          <span className="flex items-center gap-2" title="Überweisung – Rechnung wird per Bank bezahlt">
            <span className="w-4 h-4 rounded bg-emerald-500 text-white text-[10px] font-black flex items-center justify-center">Ü</span> Überweisung
          </span>
          {restplatz > 0 && (
            <span className="flex items-center gap-2" title="Notfall-Sitze mit freier Sicht – nur bei Ausverkauf freigeben">
              <span className="w-4 h-4 rounded bg-amber-400" style={{ backgroundImage: restplatzStripes }} /> Notsitz <strong>{restplatz}</strong>
            </span>
          )}
        </div>
        <span className="text-zinc-500 text-xs">{data.total} Sitze gesamt</span>
      </div>
    </div>
  );
}
